/* Local persistence for the workbench (CR-020).
 *
 * The system under construction is HIS. Each build step starts from what he
 * actually wrote last time, not from my canonical version — otherwise a design
 * choice made in step 3 silently disappears in step 4 and it stops being his
 * app. The reference implementation stays one click away, so a botched step
 * never blocks the next one.
 *
 * IndexedDB, and it never leaves the device. ADR-02 says the server never
 * executes learner code and `withoutLearnerCode()` strips any `code` field at
 * the sync ingress; persisting drafts server-side would walk straight through
 * that decision. Nothing in this file talks to the network.
 */

const DB_NAME = 'ascent-build';
const DB_VERSION = 1;
const STORE = 'drafts';

export interface RunRecord {
  at: string;
  passed: boolean;
  /** The prediction option chosen before this run, when it was the first one. */
  predicted: string | null;
  predictionCorrect: boolean | null;
  failedChecks: string[];
}

export interface BuildDraft {
  unitId: string;
  /** The learner's current source. */
  code: string;
  /** Set once, before the first run, and never editable afterwards. */
  prediction: string | null;
  runs: RunRecord[];
  updatedAt: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'unitId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB unavailable'));
  });
  return dbPromise;
}

/* Every read is best-effort. A private-mode browser with IndexedDB disabled
 * must still be able to do the task — it just will not remember it, which is a
 * degraded experience rather than a broken one. */
export async function loadDraft(unitId: string): Promise<BuildDraft | null> {
  try {
    const db = await open();
    return await new Promise<BuildDraft | null>((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(unitId);
      req.onsuccess = () => resolve((req.result as BuildDraft | undefined) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function saveDraft(draft: BuildDraft): Promise<void> {
  try {
    const db = await open();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ ...draft, updatedAt: new Date().toISOString() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* no persistence available; the session still works */
  }
}

/**
 * The task to resume on launch (CR-021): the one touched most recently.
 *
 * The app opens on the work, so it has to know which work. This is the cheapest
 * honest answer and it needs no server: `updatedAt` is already stamped on every
 * save. Returns null on a first run, and the caller falls back to the release.
 */
export async function latestDraft(): Promise<BuildDraft | null> {
  const all = await exportAll();
  if (all.length === 0) return null;
  return all.reduce((best, d) => (d.updatedAt > best.updatedAt ? d : best));
}

/** Export every draft, so a browser-storage wipe is recoverable rather than fatal. */
export async function exportAll(): Promise<BuildDraft[]> {
  try {
    const db = await open();
    return await new Promise<BuildDraft[]>((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as BuildDraft[]) ?? []);
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}
